import { getCurrentInstance, onMounted, onUnmounted } from 'vue';

const CONNECTION_LOST_STATES = ['unavailable', 'failed', 'disconnected'];

export function useSocketConnection({ channel, event, callback, onReconnect }) {
    const instance = getCurrentInstance();

    if (!instance) {
        throw new Error(
            'getCurrentInstance() returned null. Make sure you are properly calling this function in setup() or created()'
        );
    }

    const { $echo } = instance.appContext.config.globalProperties;

    if (!$echo) {
        throw new Error('Echo instance is not found. Please check if echo is properly installed.');
    }

    let echoChannel = null;
    let sawConnectionLoss = false;
    let boundConnection = null;
    let boundSubscription = null;

    const handleConnectionStateChange = ({ previous, current }) => {
        // pusher-js only reaches `unavailable` after unavailableTimeout (10s) on
        // top of its retry cycle: a short drop is connected -> connecting ->
        // connected and an explicit disconnect is connected -> disconnected.
        // Leaving `connected` is the loss signal, not just the terminal states.
        if (current !== 'connected' && (previous === 'connected' || CONNECTION_LOST_STATES.includes(current))) {
            sawConnectionLoss = true;

            return;
        }

        // Only a reconnect (connected again after a real loss) triggers the
        // callback — the initial connection must not.
        if (current === 'connected' && sawConnectionLoss) {
            sawConnectionLoss = false;

            onReconnect();
        }
    };

    const handleSubscriptionError = () => {
        // A failed (re)subscription means broadcasts were possibly missed, the
        // same blind spot as a dropped connection: let the consumer re-sync.
        onReconnect();
    };

    const setupReconnectListener = () => {
        if (typeof onReconnect !== 'function' || boundConnection) return;

        // Echo's Pusher connector exposes the underlying connection state
        // machine; other drivers (or a mocked Echo in tests) may not.
        const connection = $echo.connector?.pusher?.connection;

        if (connection?.bind) {
            connection.bind('state_change', handleConnectionStateChange);

            boundConnection = connection;
        }

        // `echoChannel.error()` binds an anonymous wrapper internally, which
        // can never be unbound — handlers pile up on Pusher's cached channel
        // across mount/unmount cycles. Bind the tracked handler directly on
        // the underlying subscription so it can be unbound on cleanup.
        const subscription = echoChannel?.subscription;

        if (subscription?.bind) {
            subscription.bind('pusher:subscription_error', handleSubscriptionError);

            boundSubscription = subscription;
        }
    };

    const removeReconnectListener = () => {
        if (boundConnection) {
            boundConnection.unbind?.('state_change', handleConnectionStateChange);

            boundConnection = null;
        }

        if (boundSubscription) {
            boundSubscription.unbind?.('pusher:subscription_error', handleSubscriptionError);

            boundSubscription = null;
        }
    };

    const setupEchoListener = () => {
        if (echoChannel) return;

        echoChannel = $echo.private(channel).listen(event, async payload => {
            if (typeof callback === 'function') {
                await callback(payload);
            }
        });

        setupReconnectListener();
    };

    const removeEchoListener = () => {
        if (echoChannel) {
            echoChannel.stopListening(event);

            echoChannel = null;
        }

        removeReconnectListener();
    };

    onMounted(() => {
        setupEchoListener();
    });

    onUnmounted(() => {
        removeEchoListener();
    });
}
